import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import type {
  Category,
  CategoryFilters,
  CategoryListResponseDto,
  CategoryWithDetails,
} from "../types/category";
import { 
  buildCategoryTree, 
  getActiveCategories, 
} from "../types/category";

// ==========================================
// HOOKS PRINCIPALES (NUEVOS)
// ==========================================

/**
 * Hook principal para obtener categorías con paginación y filtros
 */
export function useCategories(filters?: CategoryFilters) {
  return useQuery({
    queryKey: ["categories", filters],
    queryFn: async (): Promise<CategoryListResponseDto> => {
      const params = new URLSearchParams();

      // Filtros del nuevo backend
      if (filters?.catalogId) params.append("catalogId", filters.catalogId);
      if (filters?.parentId !== undefined) {
        if (filters.parentId === null) {
          params.append("parentId", "null");
        } else {
          params.append("parentId", filters.parentId);
        }
      }
      if (filters?.active !== undefined) {
        params.append("active", filters.active.toString());
      }
      if (filters?.featured !== undefined) {
        params.append("featured", filters.featured.toString());
      }
      if (filters?.name) params.append("name", filters.name);
      if (filters?.includeDeleted !== undefined) {
        params.append("includeDeleted", filters.includeDeleted.toString());
      }
      if (filters?.sortField) params.append("sortField", filters.sortField);
      if (filters?.sortDirection) params.append("sortDirection", filters.sortDirection);

      const response = await apiClient.get<CategoryListResponseDto>(
        `/categories?${params.toString()}`
      );
      
      // 🏷️ LOG: Data CRUDA del backend para categorías (sin transformaciones)
      console.log("🏷️ [CATEGORIES] Data CRUDA del backend:", response.data);
      
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
}

/**
 * Hook para obtener una categoría por ID
 */
export function useCategory(id: string) {
  return useQuery({
    queryKey: ["category", id],
    queryFn: async (): Promise<Category> => {
      const response = await apiClient.get<{ success: boolean; data: Category }>(`/category/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Hook para obtener una categoría por slug (SEO-friendly)
 * NUEVO endpoint del backend
 */
export function useCategoryBySlug(slug: string, catalogId?: string) {
  return useQuery({
    queryKey: ["category", "slug", slug, catalogId],
    queryFn: async (): Promise<Category> => {
      const params = catalogId ? `?catalogId=${catalogId}` : "";
      const response = await apiClient.get<{ success: boolean; data: Category }>(
        `/category/slug/${slug}${params}`
      );
      return response.data;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 10, // 10 minutos (SEO content cambia poco)
    gcTime: 1000 * 60 * 30, // 30 minutos
  });
}

/**
 * Hook para obtener categorías por catálogo
 */
export function useCategoriesByCatalog(catalogId: string) {
  return useQuery({
    queryKey: ["categories", "catalog", catalogId],
    queryFn: async (): Promise<CategoryListResponseDto> => {
      const response = await apiClient.get<CategoryListResponseDto>(
        `/category/catalog/${catalogId}`
      );
      return response;
    },
    enabled: !!catalogId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Hook para obtener solo categorías root de un catálogo
 */
export function useRootCategoriesByCatalog(catalogId: string) {
  return useQuery({
    queryKey: ["categories", "root", "catalog", catalogId],
    queryFn: async (): Promise<CategoryListResponseDto> => {
      const response = await apiClient.get<CategoryListResponseDto>(
        `/category/catalog/${catalogId}/root`
      );
      
      // 🏷️ LOG: Data CRUDA del backend para categorías root (sin transformaciones)
      console.log("🏷️ [ROOT CATEGORIES] Data CRUDA del backend:", response.data);
      
      return response;
    },
    enabled: !!catalogId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Hook para obtener subcategorías de una categoría padre
 */
export function useSubcategoriesByParent(parentId: string) {
  return useQuery({
    queryKey: ["categories", "subcategories", parentId],
    queryFn: async (): Promise<CategoryListResponseDto> => {
      const response = await apiClient.get<CategoryListResponseDto>(
        `/category/${parentId}/subcategories`
      );
      return response;
    },
    enabled: !!parentId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Hook para obtener categorías destacadas
 * NUEVO endpoint del backend
 */
export function useFeaturedCategories(limit?: number, catalogId?: string) {
  return useQuery({
    queryKey: ["categories", "featured", limit, catalogId],
    queryFn: async (): Promise<Category[]> => {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      if (catalogId) params.append("catalogId", catalogId);
      
      const queryString = params.toString();
      const response = await apiClient.get<{ success: boolean; data: Category[] }>(
        `/category/featured${queryString ? `?${queryString}` : ""}`
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutos
    gcTime: 1000 * 60 * 30, // 30 minutos
  });
}

// ==========================================
// HOOKS DERIVADOS (CONSTRUYEN DATOS EN FRONTEND)
// ==========================================

/**
 * Hook para obtener categorías activas de un catálogo específico
 * Refactorizado: usa filtros en lugar de endpoint dedicado
 */
export function useActiveCategoriesByCatalog(catalogId: string) {
  const { data, ...rest } = useCategoriesByCatalog(catalogId);
  
  return {
    ...rest,
    data: data ? {
      ...data,
      items: getActiveCategories(data.data.items)
    } : undefined
  };
}

/**
 * Hook para categorías raíz (sin padre)
 * Refactorizado: usa filtros con parentId=null
 */
export function useRootCategories(catalogId?: string) {
  const { data, ...rest } = useCategories({
    catalogId,
    parentId: null,
    active: true
  });
  
  return {
    ...rest,
    data: data?.data.items || []
  };
}

/**
 * Hook para árbol de categorías (estructura jerárquica)
 * Refactorizado: construye el árbol en el frontend
 */
export function useCategoryTree(catalogId?: string) {
  const { data, ...rest } = useCategories({
    catalogId,
    active: true
  });
  
  return {
    ...rest,
    data: data ? buildCategoryTree(data.data.items) : []
  };
}

// ==========================================
// HOOKS LEGACY (COMPATIBILIDAD TEMPORAL)
// ==========================================

/**
 * Hook legacy para categorías con detalles
 * DEPRECADO: El nuevo backend no usa ?include=details
 * Usar useCategory() en su lugar
 */
export function useCategoryWithDetails(id: string) {
  return useQuery({
    queryKey: ["category", id, "with-details"],
    queryFn: async (): Promise<CategoryWithDetails> => {
      const response = await apiClient.get<{ success: boolean; data: Category }>(`/category/${id}`);
      const category = response.data;
      
      // Construir CategoryWithDetails manualmente
      return {
        ...category,
        catalog: undefined, // Se puede obtener por separado si se necesita
        parentCategory: undefined, // Se puede obtener por separado si se necesita
        children: [] // Se puede construir con useCategoryTree si se necesita
      };
    },
    enabled: !!id,
  });
}

// ==========================================
// HOOKS DE UTILIDAD
// ==========================================

/**
 * Hook para obtener subcategorías de una categoría específica
 */
export function useSubcategories(parentId: string) {
  return useQuery({
    queryKey: ["categories", "subcategories", parentId],
    queryFn: async (): Promise<Category[]> => {
      const response = await apiClient.get<CategoryListResponseDto>(
        `/categories?parentId=${parentId}&active=true`
      );
      return response.data.items;
    },
    enabled: !!parentId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Hook para buscar categorías por nombre
 */
export function useCategorySearch(query: string, catalogId?: string) {
  return useQuery({
    queryKey: ["categories", "search", query, catalogId],
    queryFn: async (): Promise<Category[]> => {
      const params = new URLSearchParams();
      params.append("name", query);
      params.append("active", "true");
      if (catalogId) params.append("catalogId", catalogId);

      const response = await apiClient.get<CategoryListResponseDto>(
        `/categories?${params.toString()}`
      );
      return response.data.items;
    },
    enabled: !!query && query.length >= 2,
    staleTime: 1000 * 60 * 2, // 2 minutos (búsqueda más dinámica)
    gcTime: 1000 * 60 * 5,
  });
}

/**
 * Hook para obtener estadísticas de categorías
 * Combina múltiples hooks para obtener estadísticas
 */
export function useCategoryStats(catalogId?: string) {
  const categoriesQuery = useCategories({
    catalogId,
    includeDeleted: true
  });
  
  const categories = categoriesQuery.data?.data.items || [];
  
  const stats = {
    total: categories.length,
    active: categories.filter(c => c.isActive).length,
    inactive: categories.filter(c => !c.isActive).length,
    featured: categories.filter(c => c.isFeatured).length,
    deleted: categories.filter(c => c.isDeleted).length,
    root: categories.filter(c => c.parentId === null).length,
    withChildren: categories.filter(c => 
      categories.some(child => child.parentId === c.id)
    ).length
  };
  
  return {
    ...categoriesQuery,
    data: stats
  };
}

